import { createSlice } from '@reduxjs/toolkit';
import flatten from 'lodash/flatten';
import keyBy from 'lodash/keyBy';
import xorBy from 'lodash/xorBy';
import { FilterSliceState } from '../../types';
import { getInitialUrlData, getInitialIsActiveBrand } from '../../utils';

const { initialAppFilters, initialViewFilter } = getInitialUrlData();

export const initialState: FilterSliceState = {
  currentFilters: [],
  appFilters: initialAppFilters,
  searchToggle: initialViewFilter,
  multiSelectToggle: false,
  selectAllToggle: false,
  selectedMaterials: [],
  overrideParams: undefined,
  isFilterShown: true, //window.innerWidth >= 900 && !isHomePage(),
  isFiltersDrawerShown: false,
  isColorCircleTopbarShown: false,
  isBrandSwitch: false,
  isRainbowClicked: false,
  mobileActiveFilters: [],
  isRepToolEnabled: getInitialIsActiveBrand(),
  taxonomyFilterValue: undefined,
  additionalFilters: undefined,
};

const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setCurrentFilters(state, { payload }) {
      state.currentFilters = payload;
    },
    setAppFilters(state, { payload }) {
      const filtersMap = keyBy(payload, 'id');
      state.appFilters = filtersMap;
    },
    setAdditionalFilters(state, { payload }) {
      state.additionalFilters = payload;
    },
    addFilter(state, { payload }) {
      const filters = flatten([payload]);

      filters.forEach((filter) => {
        if (state.appFilters) {
          state.appFilters[filter.id] = {
            ...filter,
          };
        }
      });
    },
    removeFilter(state, { payload }) {
      const filters = flatten([payload]);
      filters.forEach((filter) => {
        const id = typeof filter === 'string' ? filter : filter.id;
        delete state.appFilters?.[id];
      });
      // Feature for SearchForm quick filters sync
      window.dispatchEvent(
        new CustomEvent('searchFilterRemove', { detail: { filters } }),
      );
    },
    clearFilters(state, { payload }) {
      // Taxonomy goes apart from all other filters
      const excluded = Array.isArray(payload) ? payload : [];
      const filteredFilters = Object.fromEntries(
        Object.entries(state.appFilters || {}).filter(([key, filter]) => {
          return excluded?.includes(filter.field);
        }),
      );
      state.appFilters = filteredFilters;
    },
    setSearchToggle(state, { payload }) {
      state.searchToggle = payload;
    },
    setMultiSelectToggle(state, { payload }) {
      state.multiSelectToggle = payload;
    },
    setSelectAllToggle(state, { payload }) {
      state.selectAllToggle = payload;
    },
    setSelectedMaterials(state, { payload }) {
      state.selectedMaterials = payload;
    },
    toggleSelectedMaterial(state, { payload }) {
      state.selectedMaterials = xorBy(
        state.selectedMaterials,
        [payload],
        'entityId',
      );
    },
    setOverrideParams(state, { payload }) {
      state.overrideParams = payload;
    },
    toggleFiltersSidebar(state, { payload }) {
      state.isFilterShown = payload ?? !state.isFilterShown;
    },
    pushMobileActiveFilters(state, { payload }) {
      if (!state.mobileActiveFilters) {
        state.mobileActiveFilters = [];
      }
      state.mobileActiveFilters.push(payload);
    },
    popMobileActiveFilters(state) {
      state.mobileActiveFilters?.pop();
    },
    resetMobileActiveFilters(state) {
      state.mobileActiveFilters = [];
    },
    setIsColorCircleTopbarShown(state, { payload }) {
      state.isColorCircleTopbarShown =
        payload ?? !state.isColorCircleTopbarShown;
    },
    setIsRainbowClicked(state, { payload }) {
      state.isRainbowClicked = payload;
    },
    setIsBrandSwitch(state, { payload }) {
      state.isBrandSwitch = payload;
    },
    setIsRepToolEnabled(state, { payload }) {
      state.isRepToolEnabled = payload;
    },
    setTaxonomyFilterValue(state, { payload }) {
      state.taxonomyFilterValue = payload;
    },
  },
});

export const {
  setCurrentFilters,
  setAppFilters,
  setSearchToggle,
  setMultiSelectToggle,
  setSelectAllToggle,
  setSelectedMaterials,
  toggleSelectedMaterial,
  setOverrideParams,
  clearFilters,
  removeFilter,
  addFilter,
  toggleFiltersSidebar,
  pushMobileActiveFilters,
  popMobileActiveFilters,
  resetMobileActiveFilters,
  setIsColorCircleTopbarShown,
  setIsRainbowClicked,
  setIsBrandSwitch,
  setIsRepToolEnabled,
  setTaxonomyFilterValue,
  setAdditionalFilters,
} = filterSlice.actions;

export default filterSlice.reducer;
